import { Fragment, useMemo, useState, type JSX } from 'react'
import {
  SessionExpiredError,
  type SetFreeeCostTagInput,
  type FreeeCostTransaction,
  type FreeeCostsByLabelMonth,
  type FreeeRecurringCost,
} from '../api'
import { compareYen, dayLabel, monthLabel, sumYen, yen } from '../format'
import {
  DEFAULT_SORT,
  expenseLines,
  inPastYear,
  linesByVendor,
  nextSort,
  pastYearSpend,
  sortStreams,
  type RecurringSort,
  type RecurringSortKey,
} from '../freeeCosts'

// The "Costs (freee)" tab. Two views the CEO asked for:
//   - the MAIN view (default): recurring costs grouped by label, each stream's
//     display name and label editable inline (the override supersedes freee's
//     account-item category and cleans up the raw bank string), plus a
//     recurring STATUS — still ongoing, assumed canceled (with the last
//     payment date), or undetermined, with a manual label for the last case;
//   - a RAW transaction view for anyone who just wants the line items.
// The recurring view can be narrowed to one label and sorted by any column
// (Latest / Past year / Total for a spend audit), and clicking a vendor opens
// the freee lines behind its numbers.
// Above both, a "costs per month by label" pivot — the headline "costs from
// freee per month". All of it renders data the read API already returns; the
// only write is the label/display/status override, which the backend audits.

type Props = {
  byLabel: FreeeCostsByLabelMonth[]
  recurring: FreeeRecurringCost[]
  transactions: FreeeCostTransaction[]
  // Date "past year" is measured back from (today, clamped to the last freee sync).
  asOf: string
  // Streams whose drill-down starts open (the static preview, which has no JS).
  initialExpandedVendors?: string[]
  // Persists a label/name/status override (the full dashboard's setFreeeCostTag,
  // or the IT-scoped setItCostTag). The caller binds the endpoint and token.
  saveTag: (input: SetFreeeCostTagInput) => Promise<unknown>
  // Extra line in the inline editor, e.g. what moving a vendor out of a scope does.
  editNote?: string
  onReload: () => void
  onSessionExpired: (message: string) => void
}

const CADENCE_LABEL: Record<string, string> = {
  monthly: 'Monthly',
  annual: 'Annual',
  irregular: 'Irregular',
  one_off: 'One-off',
}

// effectiveStatus values. 'canceled' only ever comes from a manual label; an
// inferred stop is 'assumed_canceled' — the wording keeps that honest.
const STATUS_LABEL: Record<string, string> = {
  ongoing: 'Ongoing',
  assumed_canceled: 'Assumed canceled',
  canceled: 'Canceled',
  undetermined: 'Undetermined',
}

// Pivot-cell key: label + month. Safe to space-join even though a label may
// contain spaces ("IT Software Subscriptions"): month is always a fixed 10-char
// ISO date at the END of the key, so two distinct (label, month) pairs can never
// produce the same string (the trailing 10 chars pin the month, the rest the label).
function cellKey(label: string, month: string): string {
  return `${label} ${month}`
}

export function FreeeCostsView({
  byLabel,
  recurring,
  transactions,
  asOf,
  initialExpandedVendors = [],
  saveTag,
  editNote,
  onReload,
  onSessionExpired,
}: Props): JSX.Element {
  const [subview, setSubview] = useState<'recurring' | 'raw'>('recurring')

  const hasData = byLabel.length > 0 || recurring.length > 0 || transactions.length > 0
  if (!hasData) {
    return (
      <section className="panel">
        <h2>Costs (freee)</h2>
        <p className="notice">
          No freee cost data yet. It appears once the freee sync has run (or after <strong>Refresh data</strong>, for
          full-dashboard users), then reload.
        </p>
      </section>
    )
  }

  return (
    <>
      <MonthlyCostsByLabel rows={byLabel} />
      <section className="panel">
        <header className="chart-head">
          <div>
            <h2>{subview === 'recurring' ? 'Recurring costs by label' : 'Raw transactions'}</h2>
            <p className="chart-sub">
              {subview === 'recurring'
                ? 'Costs that reoccur, grouped by label. Status says whether a stream still looks alive: assumed canceled once a cadence interval plus a buffer passes unpaid. Click a column heading to sort, or a vendor to see the freee lines behind it. Rename a vendor, move it to a different label, or pin the status via Edit.'
                : // Deposits filtered in the DB (migration 20261029100000); restore "Every freee cost line synced, newest first." in the same revert.
                  'Every freee cost line synced except Deposits / 預り金, newest first.'}
            </p>
          </div>
          <ul className="subtabs" role="tablist" aria-label="Cost views">
            <li>
              <button
                type="button"
                role="tab"
                id="freee-subtab-recurring"
                aria-controls="freee-subpanel-recurring"
                aria-selected={subview === 'recurring'}
                className={subview === 'recurring' ? 'active' : ''}
                onClick={() => setSubview('recurring')}
              >
                Recurring
              </button>
            </li>
            <li>
              <button
                type="button"
                role="tab"
                id="freee-subtab-raw"
                aria-controls="freee-subpanel-raw"
                aria-selected={subview === 'raw'}
                className={subview === 'raw' ? 'active' : ''}
                onClick={() => setSubview('raw')}
              >
                Raw transactions
              </button>
            </li>
          </ul>
        </header>

        {/* Both sub-views stay MOUNTED; the inactive one is hidden. Unmounting on
            switch would discard an in-progress inline label/name edit — the same
            "don't silently lose work" care as the top-level tab panels. */}
        <div hidden={subview !== 'recurring'} role="tabpanel" id="freee-subpanel-recurring" aria-labelledby="freee-subtab-recurring">
          <RecurringByLabel
            recurring={recurring}
            transactions={transactions}
            asOf={asOf}
            initialExpandedVendors={initialExpandedVendors}
            saveTag={saveTag}
            editNote={editNote}
            onReload={onReload}
            onSessionExpired={onSessionExpired}
          />
        </div>
        <div hidden={subview !== 'raw'} role="tabpanel" id="freee-subpanel-raw" aria-labelledby="freee-subtab-raw">
          <RawTransactions transactions={transactions} />
        </div>
      </section>
    </>
  )
}

// The headline pivot: labels down the side, months across the top. Same shape
// as the GOSPEL masthead so it reads as part of the same dashboard. Every total
// is summed with `sumYen` (decimal-exact) rather than JS-float addition — a
// pivot total that drifts a yen from Postgres is the bug this project kills.
function MonthlyCostsByLabel({ rows }: { rows: FreeeCostsByLabelMonth[] }): JSX.Element | null {
  if (rows.length === 0) return null
  const months = [...new Set(rows.map((r) => r.month))].sort()
  // The read view emits one row per (effective label, month), so each key is unique.
  const cell = new Map<string, string>()
  for (const r of rows) cell.set(cellKey(r.effectiveLabel, r.month), r.totalAmount)
  const cellFor = (label: string, m: string): string | null => cell.get(cellKey(label, m)) ?? null

  // Per-label totals computed ONCE, then ordered heaviest-first — decorating the
  // sort keeps the total out of the comparator (no O(n²) re-summing per compare).
  const orderedLabels = [...new Set(rows.map((r) => r.effectiveLabel))]
    .map((label) => ({ label, total: sumYen(months.map((m) => cellFor(label, m))) }))
    .sort((a, b) => compareYen(b.total, a.total))
  const monthTotal = (m: string): string => sumYen(orderedLabels.map(({ label }) => cellFor(label, m)))
  const grandTotal = sumYen(orderedLabels.map(({ total }) => total))

  return (
    <section className="panel">
      <h2>Costs from freee, per month</h2>
      <div className="scroller">
        <table>
          <thead>
            <tr>
              <th scope="col" className="rowhead">
                Label
              </th>
              {months.map((m) => (
                <th scope="col" key={m}>
                  {monthLabel(m)}
                </th>
              ))}
              <th scope="col">Total</th>
            </tr>
          </thead>
          <tbody>
            {orderedLabels.map(({ label, total }) => (
              <tr key={label}>
                <th scope="row" className="rowhead">
                  {label}
                </th>
                {months.map((m) => {
                  const v = cellFor(label, m)
                  return <td key={m}>{v === null ? <span className="muted">—</span> : yen(v)}</td>
                })}
                <td>{yen(total)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <th scope="row" className="rowhead">
                All labels
              </th>
              {months.map((m) => (
                <td key={m}>{yen(monthTotal(m))}</td>
              ))}
              <td>{yen(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  )
}

// A cost line's amount: a credit (income side) in brackets and red, so it never
// reads as spend. Shared by the drill-down and the raw view so they agree.
function AmountCell({ line }: { line: FreeeCostTransaction }): JSX.Element {
  const credit = line.entrySide === 'income'
  return <td className={credit ? 'negative' : ''}>{credit ? `(${yen(line.amount)})` : yen(line.amount)}</td>
}

function CadenceBadge({ cadence }: { cadence: string }): JSX.Element {
  return <span className={`badge cadence-${cadence}`}>{CADENCE_LABEL[cadence] ?? cadence}</span>
}

// The recurring-status cell: badge + the context that justifies it. A canceled
// stream (assumed or manual) shows WHEN money last left; a manually-labelled
// one says so, so an override never masquerades as an inference.
function StatusCell({ row }: { row: FreeeRecurringCost }): JSX.Element {
  const status = row.effectiveStatus
  const showLastPaid = status === 'assumed_canceled' || status === 'canceled'
  return (
    <td>
      <span className={`badge status-${status}`}>{STATUS_LABEL[status] ?? status}</span>
      {row.statusOverride !== null && <span className="badge overridden">manual</span>}
      {showLastPaid && <span className="status-detail">last paid {dayLabel(row.lastPaymentDate)}</span>}
      {status === 'undetermined' && <span className="status-detail">not enough history — set it via Edit</span>}
    </td>
  )
}

// A sortable column heading. One sort applies to every label group, so moving
// between groups never changes what "top of the table" means.
function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
  title,
}: {
  label: string
  sortKey: RecurringSortKey
  sort: RecurringSort
  onSort: (key: RecurringSortKey) => void
  className?: string
  title?: string
}): JSX.Element {
  const active = sort.key === sortKey
  return (
    <th
      scope="col"
      className={className}
      title={title}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className={`sort-head${active ? ' active' : ''}`} onClick={() => onSort(sortKey)}>
        {label}
        <span className="sort-arrow" aria-hidden="true">
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  )
}

// The drill-down under a stream: the freee lines behind its numbers, newest
// first. The expense lines must add up to the stream's Total (both come from
// the same freee_costs rows); if they don't, say so rather than show a list
// that silently disagrees with the row above it.
function VendorLines({
  row,
  lines,
  asOf,
}: {
  row: FreeeRecurringCost
  lines: FreeeCostTransaction[]
  asOf: string
}): JSX.Element {
  const expenses = expenseLines(lines)
  const credits = lines.length - expenses.length
  const linesTotal = sumYen(expenses.map((t) => t.amount))
  const mismatch = compareYen(linesTotal, row.totalAmount) !== 0
  return (
    <div className="drill">
      {lines.length === 0 ? (
        <p className="notice">No freee lines found for this vendor.</p>
      ) : (
        <table className="drill-table">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Description</th>
              <th scope="col">Ref</th>
              <th scope="col">freee label</th>
              <th scope="col">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((t) => (
              <tr key={t.costKey} className={inPastYear(t.issueDate, asOf) ? '' : 'drill-older'}>
                <td className="secondary">{dayLabel(t.issueDate)}</td>
                <td>{t.description ?? <span className="muted">—</span>}</td>
                <td className="secondary">{t.refNumber ?? '—'}</td>
                <td className="secondary">{t.freeeLabel ?? '(uncategorized)'}</td>
                <AmountCell line={t} />
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="drill-foot">
        {expenses.length} payment{expenses.length === 1 ? '' : 's'} · {yen(linesTotal)} total ·{' '}
        {yen(pastYearSpend(lines, asOf))} in the 12 months to {dayLabel(asOf)}. Lines outside that window are
        dimmed.
        {credits > 0 &&
          ` ${credits} credit line${credits === 1 ? '' : 's'} in brackets ${credits === 1 ? 'is' : 'are'} not counted (totals are expenses only).`}
      </p>
      {mismatch && (
        <p className="error" role="alert">
          These lines add up to {yen(linesTotal)}, but the stream’s Total is {yen(row.totalAmount)}. The line
          list may be incomplete; reload, and if it persists, flag it.
        </p>
      )}
    </div>
  )
}

// The editable main view. Streams grouped by effective label, recurring cadence
// first by default; any column heading re-sorts every group. A label filter
// narrows to one group (e.g. IT Software Subscriptions). Each row edits inline;
// a save re-reads the whole dashboard so the new effective label/name shows
// everywhere (no cache, by design). Clicking a vendor opens its freee lines.
function RecurringByLabel({
  recurring,
  transactions,
  asOf,
  initialExpandedVendors,
  saveTag,
  editNote,
  onReload,
  onSessionExpired,
}: {
  recurring: FreeeRecurringCost[]
  transactions: FreeeCostTransaction[]
  asOf: string
  initialExpandedVendors: string[]
  saveTag: (input: SetFreeeCostTagInput) => Promise<unknown>
  editNote: string | undefined
  onReload: () => void
  onSessionExpired: (message: string) => void
}): JSX.Element {
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftLabel, setDraftLabel] = useState('')
  // '' = no manual label (trust the inference); 'ongoing' | 'canceled' pin it.
  const [draftStatus, setDraftStatus] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sort, setSort] = useState<RecurringSort>(DEFAULT_SORT)
  // '' = every label.
  const [labelFilter, setLabelFilter] = useState('')
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(initialExpandedVendors))

  // Per-vendor lines and past-year spend over the full synced history, computed
  // once per data load, not on every keystroke in the inline editor below.
  const lines = useMemo(() => linesByVendor(transactions), [transactions])
  const pastYearByVendor = useMemo(
    () => new Map(recurring.map((r) => [r.vendorKey, pastYearSpend(lines.get(r.vendorKey) ?? [], asOf)])),
    [recurring, lines, asOf],
  )

  if (recurring.length === 0) {
    return <p className="notice">No recurring cost streams found yet.</p>
  }

  const startEdit = (row: FreeeRecurringCost): void => {
    setEditingKey(row.vendorKey)
    setDraftName(row.displayNameOverride ?? '')
    setDraftLabel(row.labelOverride ?? '')
    setDraftStatus(row.statusOverride ?? '')
    setError(null)
  }

  const toggleExpanded = (vendorKey: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(vendorKey)) next.delete(vendorKey)
      else next.add(vendorKey)
      return next
    })
  }

  const save = async (vendorKey: string): Promise<void> => {
    setSaving(true)
    setError(null)
    try {
      await saveTag({
        vendorKey,
        displayName: draftName.trim() || null,
        labelOverride: draftLabel.trim() || null,
        statusOverride: draftStatus || null,
      })
      setEditingKey(null)
      onReload()
    } catch (cause) {
      if (cause instanceof SessionExpiredError) {
        onSessionExpired(cause.message)
        return
      }
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setSaving(false)
    }
  }

  const linesFor = (vendorKey: string): FreeeCostTransaction[] => lines.get(vendorKey) ?? []
  const pastYear = (vendorKey: string): string => pastYearByVendor.get(vendorKey) ?? '0.00'

  // How far back "Total" reaches: the earliest month freee has synced for any stream.
  const historyStart = recurring.reduce((min, r) => (r.firstMonth < min ? r.firstMonth : min), recurring[0]?.firstMonth ?? asOf)

  // Group by effective label. Group totals are summed decimal-exactly and ONCE
  // (decorate-sort-undecorate), so the comparator never re-sums a group. Groups
  // order by past-year spend when that is the sort, otherwise by total; under
  // an ascending Total / Past year sort the groups go smallest-first too, so
  // the smallest stream isn't buried under the biggest label.
  const groups = new Map<string, FreeeRecurringCost[]>()
  for (const row of recurring) {
    const list = groups.get(row.effectiveLabel) ?? []
    list.push(row)
    groups.set(row.effectiveLabel, list)
  }
  const byPastYear = sort.key === 'pastYear'
  const groupSign = (sort.key === 'total' || byPastYear) && sort.dir === 'asc' ? -1 : 1
  const orderedGroups = [...groups.entries()]
    .map(([label, rows]) => ({
      label,
      rows,
      total: sumYen(rows.map((r) => r.totalAmount)),
      pastYearTotal: sumYen(rows.map((r) => pastYear(r.vendorKey))),
    }))
    .sort((a, b) =>
      groupSign * (byPastYear ? compareYen(b.pastYearTotal, a.pastYearTotal) : compareYen(b.total, a.total)),
    )
  // A filter on a label that no longer exists (re-labelled away) falls back to all.
  const activeFilter = groups.has(labelFilter) ? labelFilter : ''
  const visibleGroups = activeFilter === '' ? orderedGroups : orderedGroups.filter((g) => g.label === activeFilter)
  const onSort = (key: RecurringSortKey): void => setSort((current) => nextSort(current, key))

  return (
    <>
      {error !== null && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="recurring-toolbar">
        <label>
          Label
          <select value={activeFilter} onChange={(e) => setLabelFilter(e.target.value)}>
            <option value="">All labels</option>
            {orderedGroups.map(({ label }) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {(sort.key !== DEFAULT_SORT.key || sort.dir !== DEFAULT_SORT.dir) && (
          <button type="button" className="row-edit" onClick={() => setSort(DEFAULT_SORT)}>
            Reset sort
          </button>
        )}
        <p className="recurring-note">
          <strong>Total</strong> is everything freee has synced for the vendor (since {monthLabel(historyStart)}).{' '}
          <strong>Past year</strong> is the 12 months to {dayLabel(asOf)}.{' '}
          {/* Filtered in the DB (freee_is_excluded_cost_label, migration 20261029100000); remove this line in the same revert. */}
          Deposits / 預り金 lines are left out of these figures.
        </p>
      </div>
      {visibleGroups.map(({ label, rows, total, pastYearTotal }) => (
        <div className="label-group" key={label}>
          <h3 className="label-group-head">
            {label}{' '}
            <span className="muted">
              · {yen(pastYearTotal)} past year · {yen(total)} total
            </span>
          </h3>
          <div className="scroller">
            <table>
              <thead>
                <tr>
                  <SortHeader label="Vendor" sortKey="vendor" sort={sort} onSort={onSort} className="rowhead" />
                  <SortHeader label="Cadence" sortKey="cadence" sort={sort} onSort={onSort} />
                  <th scope="col">Status</th>
                  <SortHeader label="Months" sortKey="months" sort={sort} onSort={onSort} />
                  <SortHeader label="Latest" sortKey="latest" sort={sort} onSort={onSort} />
                  <SortHeader
                    label="Past year"
                    sortKey="pastYear"
                    sort={sort}
                    onSort={onSort}
                    title={`Paid in the 12 months to ${dayLabel(asOf)}`}
                  />
                  <SortHeader
                    label="Total"
                    sortKey="total"
                    sort={sort}
                    onSort={onSort}
                    title={`Everything freee has synced, since ${monthLabel(historyStart)}`}
                  />
                  <th scope="col" className="actions-col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortStreams(rows, sort, pastYear).map((row) => {
                  const isOpen = expanded.has(row.vendorKey)
                  const rowPastYear = pastYear(row.vendorKey)
                  const drillId = `freee-lines-${encodeURIComponent(row.vendorKey)}`
                  return (
                    <Fragment key={row.vendorKey}>
                      {editingKey === row.vendorKey ? (
                        <tr className="editing-row">
                          <td colSpan={8}>
                            <div className="tag-edit">
                              <span className="tag-edit-raw">
                                freee: <code>{row.vendorKey}</code> in <em>{row.freeeLabel ?? '(uncategorized)'}</em>
                              </span>
                              <label>
                                Display name
                                <input
                                  type="text"
                                  value={draftName}
                                  placeholder={row.freeeLabel === null ? 'freee default' : row.displayName}
                                  onChange={(e) => setDraftName(e.target.value)}
                                />
                              </label>
                              <label>
                                Label
                                <input
                                  type="text"
                                  value={draftLabel}
                                  placeholder={row.freeeLabel ?? '(uncategorized)'}
                                  onChange={(e) => setDraftLabel(e.target.value)}
                                />
                              </label>
                              <label>
                                Status
                                <select value={draftStatus} onChange={(e) => setDraftStatus(e.target.value)}>
                                  <option value="">
                                    Auto — {STATUS_LABEL[row.inferredStatus] ?? row.inferredStatus}
                                  </option>
                                  <option value="ongoing">Still ongoing</option>
                                  <option value="canceled">Canceled</option>
                                </select>
                              </label>
                              <div className="tag-edit-actions">
                                <button type="button" className="primary" disabled={saving} onClick={() => void save(row.vendorKey)}>
                                  {saving ? 'Saving…' : 'Save'}
                                </button>
                                <button type="button" disabled={saving} onClick={() => setEditingKey(null)}>
                                  Cancel
                                </button>
                              </div>
                              <p className="tag-edit-hint">
                                Leave a field blank (or status on Auto) to fall back to freee’s default and the inferred
                                status.
                              </p>
                              {editNote !== undefined && <p className="tag-edit-hint">{editNote}</p>}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr className={isOpen ? 'drill-open' : ''}>
                          <th scope="row" className="rowhead">
                            <button
                              type="button"
                              className="drill-toggle"
                              aria-expanded={isOpen}
                              aria-controls={isOpen ? drillId : undefined}
                              title="Show the freee lines behind this vendor"
                              onClick={() => toggleExpanded(row.vendorKey)}
                            >
                              <span className="drill-caret" aria-hidden="true">
                                {isOpen ? '▾' : '▸'}
                              </span>
                              {row.displayName}
                            </button>
                            {row.displayNameOverride !== null && <span className="badge overridden">renamed</span>}
                            {row.labelOverride !== null && <span className="badge overridden">re-labelled</span>}
                          </th>
                          <td>
                            <CadenceBadge cadence={row.cadence} />
                          </td>
                          <StatusCell row={row} />
                          <td>{row.distinctMonths}</td>
                          <td>{yen(row.latestAmount)}</td>
                          <td>
                            {compareYen(rowPastYear, '0') === 0 ? <span className="muted">—</span> : yen(rowPastYear)}
                          </td>
                          <td>{yen(row.totalAmount)}</td>
                          <td className="actions-col">
                            <button type="button" className="row-edit" onClick={() => startEdit(row)}>
                              Edit
                            </button>
                          </td>
                        </tr>
                      )}
                      {isOpen && (
                        <tr className="drill-row" id={drillId}>
                          <td colSpan={8}>
                            <VendorLines row={row} lines={linesFor(row.vendorKey)} asOf={asOf} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  )
}

function RawTransactions({ transactions }: { transactions: FreeeCostTransaction[] }): JSX.Element {
  if (transactions.length === 0) {
    return <p className="notice">No freee cost lines synced yet.</p>
  }
  return (
    <div className="scroller">
      <table>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col" className="rowhead">
              Vendor
            </th>
            <th scope="col">Label</th>
            <th scope="col">Description</th>
            <th scope="col">Amount</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((t) => (
            <tr key={t.costKey}>
              <td className="secondary">{t.issueDate}</td>
              <th scope="row" className="rowhead">
                {t.displayName ?? t.vendorRaw ?? '—'}
              </th>
              <td>{t.effectiveLabel ?? <span className="muted">(uncategorized)</span>}</td>
              <td className="secondary">{t.description ?? '—'}</td>
              <AmountCell line={t} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
